import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { ChevronDown, ExternalLink, Info, Loader2, QrCode, Star } from 'lucide-react';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardContent } from '@/components/ui/card';
import { HoverCard } from '@/components/ui/hover-card';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { NoDataForPeriod, isoDay } from '@/components/common/NoDataForPeriod';
import { ChipStepPicker, SearchChipInput, SplitPill, type PickerKind, type PickerValue, type SearchChip } from '@/components/common/SearchChipInput';
import { getReviewsPage, getReviewsStats, type ReplyAgg, type ReplyAxisAgg,
  type ReviewDto, type ReviewProvider, type ReviewSourceDto, type ReviewThemeStat, type Severity, type ReviewsStatsDto } from '@/api/reviews';

/** Статистика → Отзывы. Reads like the speech analysis tab: a line of actionable
 *  problems on top (what guests complain about, how much it costs in rating, how
 *  bad it is for the business), then the reviews themselves. */

const PROVIDER_LABEL: Record<ReviewProvider, string> = {
  yandex: 'Яндекс Карты',
  '2gis': '2ГИС',
  linza: 'QR-форма',
};

/** Общие темы (plugins/reviews/analysis.py THEMES) — одни для похвалы и жалоб.
 *  Старые категории первой версии разбора — для отзывов, которые ещё не
 *  перебраны заново. Слова «персонал» нет намеренно (Алексей 17.09). */
export const CATEGORY_LABEL: Record<string, string> = {
  coffee_quality: 'Качество кофе',
  food_quality: 'Качество еды',
  product_missing_food: 'Нет еды',
  product_missing_drinks: 'Нет напитков',
  service_speed: 'Скорость обслуживания',
  // «Работа команды» разбита на вежливость и профессионализм (AI105). Старый
  // ключ оставлен: пока ночной перебор не дошёл до отзыва, он приходит с ним.
  staff_manner: 'Вежливость',
  staff_skill: 'Профессионализм',
  team: 'Работа команды',
  hospitality: 'Гостеприимство',
  order_error: 'Ошибки в заказе',
  cleanliness: 'Чистота',
  atmosphere: 'Атмосфера',
  seating: 'Места и мебель',
  price: 'Цены',
  payment: 'Оплата и бонусы',
  safety_hygiene: 'Санитария и безопасность',
  overall: 'Общее впечатление',
  location: 'Расположение',
  opening_hours: 'График работы',
  amenities: 'Посуда и удобства',
  product_missing: 'Нет еды',
  product_quality: 'Качество еды',
  staff_attitude: 'Работа команды',
};

const SEVERITY_LABEL: Record<Severity, string> = {
  minor: 'мелочь', normal: 'заметно', major: 'серьёзно', critical: 'критично',
};
/** Цвет источника в баре разбивки. Нейтральные: красный у Яндекса и зелёный у
 *  2ГИС читались как «плохо» и «хорошо» (Алексей 17.09, AI26). */
const PROVIDER_BAR: Record<ReviewProvider, string> = {
  yandex: 'bg-violet-400',
  '2gis': 'bg-sky-400',
  linza: 'bg-primary',
};

/** Те же цвета для линий на графике: recharts красит атрибутом, а не классом,
 *  поэтому значения приходится дублировать. Держим рядом с PROVIDER_BAR, чтобы
 *  не разъехались — линия источника и его доля в полоске ниже обязаны совпадать
 *  по цвету (Алексей 22.09, AI129). */
const PROVIDER_STROKE: Record<ReviewProvider, string> = {
  yandex: '#a78bfa',
  '2gis': '#38bdf8',
  linza: 'var(--color-primary)',
};

/** Цвета чипов фильтра — те же, что у нажатой плашки. */
const PRAISE_TONE = 'border-emerald-500/40 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300';
const PROBLEM_TONE = 'border-primary/30 bg-primary/5 text-primary';
const UNANSWERED_TONE = 'border-amber-500/40 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300';
/** Теги в строке: хвалят — зелёный, жалобы — по серьёзности. */
const TAG_TONE: Record<Severity | 'praise', string> = {
  praise: PRAISE_TONE,
  minor: 'border-border bg-muted/50 text-foreground/80',
  normal: 'border-amber-500/30 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300',
  major: 'border-orange-500/30 bg-orange-50 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  critical: 'border-red-500/30 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300',
};

const SEVERITY_CLASS: Record<Severity, string> = {
  minor: 'bg-muted text-muted-foreground',
  normal: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  major: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

/** Отзывы приходят редко, поэтому лента не обрывается на границе периода
 *  (Алексей 16.09): кончились отзывы по фильтру — листаешь дальше и видишь
 *  прежние, отделённые заголовком с датой. Статистика наверху при этом считается
 *  ТОЛЬКО за выбранный период и от прокрутки не меняется. */
const DATE_FMT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' });
const TICK_FMT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
/** Больше точек на графике средней не рисуем — длинный период прореживается. */
const MAX_POINTS = 60;

/** Изменение оценки со знаком: «+0.02», «−0.1», «0.00». */
function signed(v: number, digits = 2): string {
  const r = Number(v.toFixed(digits));
  if (r === 0) return (0).toFixed(digits);
  return `${r > 0 ? '+' : '−'}${Math.abs(r).toFixed(digits)}`;
}
/** Вклад в среднюю без ведущего нуля и без округления до нуля: при сотнях
 *  отзывов тема сдвигает среднюю на тысячные, и «0.00» читалось как баг —
 *  «+.0005», «−.02», «+.11» (Алексей 17.09, AI24). */
function impactText(v: number): string {
  const abs = Math.abs(v);
  if (abs < 1e-6) return '0';
  const digits = Math.min(6, Math.max(2, -Math.floor(Math.log10(abs))));
  const body = abs.toFixed(digits).replace(/^0(?=\.)/, '');
  return `${v > 0 ? '+' : '−'}${body}`;
}
const deltaClass = (v: number, eps = 1e-6) => (
  Math.abs(v) < eps ? 'text-muted-foreground' : v > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'
);

/** «1 отзыв», «2 отзыва», «5 отзывов» — на плашке должно быть написано словами,
 *  а не голой цифрой (Алексей 16.09). */
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** Конструктор особого фильтра в поиске отзывов — тот же, что в речи (Алексей 12.10, #1440):
 *  улучшить / хвалят (темы периода), источник, оценка, без ответа. Выбор — те же списки id,
 *  что у чипов над лентой. */
function ReviewsChipBuilder({ data, unansweredIds, onPick }: {
  data: ReviewsStatsDto;
  unansweredIds: number[];
  onPick: (sel: { label: string; ids: number[]; quotes?: string[]; chip: SearchChip }) => void;
}) {
  const kinds: PickerKind[] = [
    { key: 'улучшить', label: 'Улучшить', tone: PROBLEM_TONE },
    { key: 'хвалят', label: 'Хвалят', tone: PRAISE_TONE },
    { key: 'источник', label: 'Источник', tone: 'border-border bg-muted/60 text-foreground' },
    { key: 'оценка', label: 'Оценка', tone: 'border-amber-500/40 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300' },
    { key: 'без ответа', label: 'Без ответа', tone: UNANSWERED_TONE },
  ];
  const themes = (kind: string) => (kind === 'хвалят' ? data.praise : data.problems) ?? [];
  const providers = [...new Set(data.reviews.map((r) => r.provider))] as ReviewProvider[];
  const byRating = (r: number) => data.reviews.filter((x) => x.rating === r);
  const valuesFor = (kind: string): PickerValue[] => {
    if (kind === 'улучшить' || kind === 'хвалят') return themes(kind).map((t) => ({ key: t.category, label: CATEGORY_LABEL[t.category] ?? t.category, count: t.count }));
    if (kind === 'источник') return providers.map((p) => ({ key: p, label: PROVIDER_LABEL[p], count: data.reviews.filter((r) => r.provider === p).length, dot: PROVIDER_BAR[p] }));
    if (kind === 'оценка') return [5, 4, 3, 2, 1].map((r) => ({ key: String(r), label: `${r}★`, count: byRating(r).length })).filter((v) => v.count);
    if (kind === 'без ответа') return [{ key: 'все', label: 'все без ответа', count: unansweredIds.length }];
    return [];
  };
  const build = (kind: string, value: string): { label: string; ids: number[]; quotes?: string[]; chip: SearchChip } | null => {
    if (kind === 'улучшить' || kind === 'хвалят') {
      const theme = themes(kind).find((t) => t.category === value);
      if (!theme) return null;
      const k = kind === 'хвалят' ? 'praise' : 'problem';
      const label = `${k}:${CATEGORY_LABEL[theme.category] ?? theme.category}`;
      return { label, ids: theme.review_ids, quotes: theme.quotes.map((q) => q.text), chip: { kind, value: CATEGORY_LABEL[theme.category] ?? theme.category, tone: kind === 'хвалят' ? PRAISE_TONE : PROBLEM_TONE } };
    }
    if (kind === 'источник') {
      const p = value as ReviewProvider;
      return { label: `source:${p}`, ids: data.reviews.filter((r) => r.provider === p).map((r) => r.id), chip: { value: PROVIDER_LABEL[p], dot: PROVIDER_BAR[p], tone: 'border-border bg-muted/60 text-foreground' } };
    }
    if (kind === 'оценка') {
      const r = Number(value);
      return { label: `rating:${r}`, ids: byRating(r).map((x) => x.id), chip: { kind: 'оценка', value: `${r}★`, tone: 'border-amber-500/40 bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300' } };
    }
    if (kind === 'без ответа') return { label: 'без ответа', ids: unansweredIds, chip: { value: 'без ответа', tone: UNANSWERED_TONE } };
    return null;
  };
  return (
    <ChipStepPicker
      title="Особый фильтр: тема, источник, оценка, без ответа"
      kinds={kinds}
      valuesFor={valuesFor}
      preview={(kind, value) => build(kind, value)?.chip ?? null}
      onPick={(kind, value) => { const sel = build(kind, value); if (sel) onPick(sel); }}
    />
  );
}

export default function AnalyticsReviewsTab({ zoneId, from, to }: { zoneId: string; from: string; to: string }) {
  const { data, isLoading } = useSWR(
    zoneId ? ['reviews-stats', zoneId, from, to] : null,
    async () => (await getReviewsStats(zoneId, from, to)).data,
    { revalidateOnFocus: false },
  );
  // Оценка ответов теперь едет вместе со статистикой (data.reply): разбор сохранён
  // при анализе отзыва, на лету модель больше не гоняем (Алексей 23.09, AI142).

  // Один фильтр на обе группы тем: и «хвалят», и «жалуются» сужают ленту одинаково,
  // а два независимых состояния давали бы два разных способа его снять.
  // Фильтр — чип в строке поиска, цветом того, на что нажали (Алексей 17.09, AI17.7в/AI19).
  const [selected, setSelected] = useState<{ label: string; ids: number[]; quotes?: string[]; chip: SearchChip } | null>(null);
  // Глубокая ссылка из «Приоритетов» (#1384). Хуки — ДО раннего return загрузки,
  // иначе их порядок меняется между рендерами (React #310, Алексей 09.10, #1412).
  // Глубокая ссылка: ?theme=problem:<category> выбирает тему,
  // как клик по чипу, один раз после загрузки; параметр снимаем, чтобы не залипал.
  const [searchParams, setSearchParams] = useSearchParams();
  const themeParam = searchParams.get('theme');
  useEffect(() => {
    if (!data || !themeParam) return;
    const [kind, category] = themeParam.split(':', 2) as ['praise' | 'problem', string];
    const list = kind === 'praise' ? data.praise : data.problems;
    const theme = list?.find((x) => x.category === category);
    if (theme) {
      const label = `${kind}:${CATEGORY_LABEL[theme.category] ?? theme.category}`;
      setSelected({
        label, ids: theme.review_ids, quotes: theme.quotes.map((q) => q.text),
        chip: { kind: kind === 'praise' ? 'хвалят' : 'улучшить', value: label, tone: kind === 'praise' ? PRAISE_TONE : PROBLEM_TONE },
      });
    }
    const next = new URLSearchParams(searchParams); next.delete('theme');
    setSearchParams(next, { replace: true });
  }, [data, themeParam]);
  // Поиск по тексту — по уже загруженной ленте (период + подгруженные прежние).
  const [draft, setDraft] = useState('');
  const [search, setSearch] = useState('');
  // Подгруженные прокруткой отзывы старше периода. Держим отдельно от периодных,
  // чтобы смена периода не тащила за собой чужой хвост.
  const [older, setOlder] = useState<ReviewDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Линии источников на графике по умолчанию скрыты: они сильно расходятся и
  // давят среднюю вниз. Возвращает их скромный переключатель (Алексей 23.09, #736).
  // Что показывает график средней: 'fair' — наше среднее; иначе провайдер —
  // только его линию его цветом (легенда, Алексей 23.09, #746).
  const [chartPick, setChartPick] = useState<string>('fair');
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setOlder([]);
    setCursor(null);
    setExhausted(false);
  }, [zoneId, from, to]);

  const periodReviews = data?.reviews ?? [];
  const loadMore = useCallback(async () => {
    if (!zoneId || loadingMore || exhausted) return;
    const before = cursor
      ?? older[older.length - 1]?.published_at
      ?? periodReviews[periodReviews.length - 1]?.published_at
      ?? `${from}T00:00:00Z`;
    setLoadingMore(true);
    try {
      const resp = await getReviewsPage(zoneId, before);
      setOlder((prev) => [...prev, ...resp.data.reviews]);
      setCursor(resp.data.next_before);
      if (!resp.data.next_before) setExhausted(true);
    } catch {
      // Молча не глотаем: без курсора следующая попытка повторит тот же запрос,
      // а бесконечный цикл здесь хуже, чем остановка ленты.
      setExhausted(true);
    } finally {
      setLoadingMore(false);
    }
  }, [zoneId, cursor, older, periodReviews, from, loadingMore, exhausted]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || exhausted) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) void loadMore();
    }, { rootMargin: '400px' });
    io.observe(node);
    return () => io.disconnect();
  }, [loadMore, exhausted]);

  if (isLoading || !data) {
    return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="size-4 animate-spin" /> Загрузка…</div>;
  }

  const hasAnySource = data.sources.length > 0 || !!data.form;
  // Линия — средняя оценка ЗА ВСЁ ВРЕМЯ к этому дню (сервер стартует с истории до
  // периода), ось времени настоящая (Алексей 16.09 п. 64, 17.09).
  // Точка на КАЖДЫЙ день периода, а не только на дни с отзывами: между ними
  // средняя держится прежней. Длинный период прореживается до MAX_POINTS —
  // простой шаг, последняя точка всегда остаётся (Алексей 17.09, AI22).
  // Источники, у которых есть лог показанной оценки. Линию рисуем только им:
  // пустая легенда путает сильнее, чем отсутствующая.
  const ratedSources = data.sources.filter((s) => (s.rating_series?.length ?? 0) > 0);
  const pickedSource = ratedSources.find((s) => s.provider === chartPick) ?? null;
  type RatingRow = { day: string; v?: number } & Record<string, string | number | undefined>;
  const ratingSeries = (() => {
    const known = new Map(data.period.by_day.filter((d) => d.avg_to_date !== null).map((d) => [d.day, d.avg_to_date as number]));
    const srcKnown = new Map(ratedSources.map((s) => [
      s.provider, new Map((s.rating_series ?? []).map((p) => [p.day, p.rating])),
    ]));
    const today = isoDay(0);
    const end = data.to < today ? data.to : today;
    const all: RatingRow[] = [];
    let v: number | null = data.period.avg_before ?? null;
    // Показанную оценку тянем вправо последним известным значением: снимок
    // делается раз в сутки и в дни, когда синхронизация не прошла, дырки в
    // линии читались бы как «оценка пропала».
    const last: Record<string, number | undefined> = {};
    for (let d = new Date(`${data.from}T00:00:00Z`); ; d.setUTCDate(d.getUTCDate() + 1)) {
      const day = d.toISOString().slice(0, 10);
      if (day > end) break;
      v = known.get(day) ?? v;
      const row: RatingRow = { day };
      if (v != null) row.v = v;
      let anySource = false;
      for (const s of ratedSources) {
        const got = srcKnown.get(s.provider)?.get(day);
        if (got != null) last[s.provider] = got;
        if (last[s.provider] != null) { row[`s_${s.provider}`] = last[s.provider]; anySource = true; }
      }
      if (v != null || anySource) all.push(row);
    }
    if (all.length <= MAX_POINTS) return all;
    const step = Math.ceil(all.length / MAX_POINTS);
    return all.filter((_, i) => i % step === 0 || i === all.length - 1);
  })();
  // Фильтр по теме применим только к периоду: у подгруженных прежних отзывов
  // разбора в этой выдаче нет, и молча подмешивать их к фильтру нельзя.
  const needle = search.trim().toLowerCase();
  const matchesSearch = (r: ReviewDto) => !needle || [
    r.text, r.author_name, r.business_reply,
    ...(r.analysis?.problems ?? []).map((p) => `${CATEGORY_LABEL[p.category] ?? ''} ${p.summary}`),
    ...(r.analysis?.praise ?? []).map((p) => (typeof p === 'object' ? `${CATEGORY_LABEL[p.category] ?? ''} ${p.summary}` : String(p))),
  ].some((x) => x && x.toLowerCase().includes(needle));
  const stream = (selected
    ? data.reviews.filter((r) => selected.ids.includes(r.id))
    : [...data.reviews, ...older]).filter(matchesSearch);
  const highlights = [...(selected?.quotes ?? []), ...(needle ? [search.trim()] : [])];
  const periodStart = `${data.from}T00:00:00`;
  const firstOlderId = stream.find((r) => r.published_at < periodStart)?.id ?? null;
  // За период пусто, а лента продолжилась прежними отзывами: «за период отзывов
  // нет» посередине, «Отзывы до…» ниже с большим отступом (Алексей 16.09, п. 16).
  const periodEmpty = !selected && !needle && stream.length > 0 && stream[0].id === firstOlderId;
  // Сколько период добавил/отнял к средней за всё время (Алексей 17.09).
  const periodShift = data.period.avg_before != null && data.period.avg_after != null
    ? data.period.avg_after - data.period.avg_before : null;
  // «Не сходится» (Алексей 21.09, AI108): на карточке источника 5,0, а линия
  // заканчивается на 4.08, и рядом это читается как ошибка. Свести их нельзя:
  // на картах оценка НЕ среднее по отзывам, свежие и доверенные весят больше.
  // По Jolly это видно арифметикой — даже если бы все 35 оценок без текста были
  // пятёрками, вышло бы 4.24 против показанных 5,0. Поэтому расхождение не
  // прячем, а называем словами под графиком, и только когда оно заметное.
  const mapsGap = (() => {
    if (data.period.avg_after == null) return null;
    const rated = data.sources.filter((s) => s.rating != null);
    if (!rated.length) return null;
    const top = rated.reduce((a, b) => (b.rating! > a.rating! ? b : a));
    return top.rating! - data.period.avg_after >= 0.2 ? top : null;
  })();
  const unansweredIds = data.period.unanswered_ids
    ?? data.reviews.filter((r) => r.provider !== 'linza' && !r.business_reply).map((r) => r.id);
  const pickProvider = (provider: ReviewProvider) => {
    const label = `source:${provider}`;
    setSelected(selected?.label === label ? null : {
      label, ids: data.reviews.filter((r) => r.provider === provider).map((r) => r.id),
      chip: { value: PROVIDER_LABEL[provider], dot: PROVIDER_BAR[provider], tone: 'border-border bg-muted/60 text-foreground' },
    });
  };
  const pickTheme = (theme: ReviewThemeStat, kind: 'praise' | 'problem') => {
    // Ключ выбора обязан включать вид темы (Алексей 21.09, AI105): одна и та же
    // «работа команды» живёт и в хорошем, и в «что улучшить», и по одному только
    // названию подсвечивались оба чипа сразу — лента при этом фильтровалась
    // правильно, врала именно подсветка.
    const label = `${kind}:${CATEGORY_LABEL[theme.category] ?? theme.category}`;
    setSelected(selected?.label === label ? null : {
      label, ids: theme.review_ids, quotes: theme.quotes.map((q) => q.text),
      chip: { kind: kind === 'praise' ? 'хвалят' : 'улучшить', value: label, tone: kind === 'praise' ? PRAISE_TONE : PROBLEM_TONE },
    });
  };

  return (
    <div className="space-y-4">
      {!hasAnySource && (
        <Card variant="section">
          <CardContent className="flex flex-col items-center gap-3 py-8 text-center">
            <QrCode className="size-8 text-muted-foreground" />
            <p className="max-w-lg text-sm text-muted-foreground">
              Подключите карточку филиала на Яндекс Картах или в 2ГИС — и печатайте QR-код, чтобы собирать отзывы прямо в
              зале и читать их здесь.
            </p>
            <Link to="/management/reviews" className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}>Настроить отзывы</Link>
          </CardContent>
        </Card>
      )}

      {(data.period.count > 0 || data.sources.length > 0) && (
        // Десктоп: ДВЕ колонки (Алексей 23.09, #733).
        // Левая: «Оценка сейчас в источниках» + «О чём пишут гости».
        // Правая: «Цифры за период» + «Ответы организации».
        // Если за период отзывов нет — показываем одни источники.
        data.period.count > 0 ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(300px,0.85fr)]">
          {/* Левая колонка — flex-col, чтобы карточка тем тянулась и уравнивала
              высоту с правой (Алексей 23.09, #739). */}
          <div className="flex flex-col gap-4">
          {data.sources.length > 0 && <SourcesStrip sources={data.sources} />}
          {(
            <Card variant="section" className="flex-1">
              <CardContent className="space-y-3 py-4">
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground">О чём пишут гости</h3>
                {data.praise.length > 0 && (
                  <ThemeGroup label="Хорошее">
                    {data.praise.map((t) => (
                      <ThemeChip key={t.category} theme={t} kind="praise"
                        active={selected?.label === `praise:${CATEGORY_LABEL[t.category] ?? t.category}`} onClick={() => pickTheme(t, 'praise')} />
                    ))}
                  </ThemeGroup>
                )}
                {data.problems.length > 0 ? (
                  <ThemeGroup label="Что улучшить">
                    {data.problems.map((t) => (
                      <ThemeChip key={t.category} theme={t} kind="problem"
                        active={selected?.label === `problem:${CATEGORY_LABEL[t.category] ?? t.category}`} onClick={() => pickTheme(t, 'problem')} />
                    ))}
                  </ThemeGroup>
                ) : (
                  <p className="text-sm text-muted-foreground">Жалоб и пожеланий от гостей за период не было.</p>
                )}
                {data.period.pending_analysis > 0 && (
                  <p className="text-xs text-muted-foreground">Разбираем ещё {data.period.pending_analysis}…</p>
                )}
              </CardContent>
            </Card>
          )}
          </div>

          {/* Правая колонка: Цифры сверху, Ответы под ними (Алексей 23.09, #733). */}
          <div className="space-y-4">
          <Card variant="section">
            <CardContent className="space-y-4 py-4">
              {data.period.count > 0 && (
                <section className="space-y-3">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Цифры за период</h3>
                    {(mapsGap || ratedSources.length > 0) && (
                      <HoverCard
                        as="span"
                        className="inline-flex cursor-help"
                        content={(
                          <div className="max-w-[260px] p-2 text-xs leading-snug text-muted-foreground">
                            Линия — наше среднее по собранным отзывам, накопительно. В легенде под графиком можно
                            выбрать источник и увидеть его показанную оценку по дням: она отличается — источник считает
                            ещё и оценки без текста и взвешивает отзывы по-своему.
                          </div>
                        )}
                        cardClassName="rounded-lg border border-border bg-card shadow-md"
                      >
                        <Info className="size-3.5 text-muted-foreground" />
                      </HoverCard>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-6 gap-y-3">
                    <div>
                      <div className="text-2xl font-semibold leading-none">{data.period.count}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{plural(data.period.count, 'отзыв', 'отзыва', 'отзывов')}</div>
                    </div>
                    {data.period.avg_rating !== null && (
                      <div>
                        <div className="flex items-baseline gap-1">
                          {/* Два знака, а не один: с одним «4.96» превращалось в «5.0», и
                              заголовок спорил со списком вычетов под ним (Алексей 18.09, AI52). */}
                          <span className="text-2xl font-semibold leading-none">{data.period.avg_rating.toFixed(2)}</span>
                          <Star className="size-4 translate-y-px fill-amber-400 text-amber-400" />
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">средняя за период</div>
                      </div>
                    )}
                  </div>
                  {periodShift != null && (
                    <p className="text-sm">
                      <span className={cn('font-semibold tabular-nums', deltaClass(periodShift))}>{impactText(periodShift)} ★</span>
                      <span className="text-muted-foreground">
                        {' '}к средней по собранным отзывам: {data.period.avg_before!.toFixed(2)} → {data.period.avg_after!.toFixed(2)}
                      </span>
                    </p>
                  )}
                  {ratingSeries.length > 1 && (
                    // Средняя за всё время к этому дню, area и крупнее (Алексей 17.09, AI17.3).
                    // Ниже — легенда: среднее или один источник его цветом (#746).
                    <div className="space-y-1.5">
                      {ratedSources.length > 0 && (
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                          <button
                            type="button"
                            onClick={() => setChartPick('fair')}
                            className={cn('inline-flex items-center gap-1.5 rounded px-1 py-0.5 transition-colors', chartPick === 'fair' ? 'font-medium text-foreground' : 'text-muted-foreground hover:text-foreground')}
                          >
                            <span className="h-1.5 w-4 rounded-full bg-primary" />
                            честное среднее
                          </button>
                          {ratedSources.map((s) => (
                            <button
                              key={s.provider}
                              type="button"
                              onClick={() => setChartPick(s.provider)}
                              className={cn('inline-flex items-center gap-1.5 rounded px-1 py-0.5 transition-colors', chartPick === s.provider ? 'font-medium text-foreground' : 'text-muted-foreground hover:text-foreground')}
                            >
                              <span className="size-2 rounded-full" style={{ background: PROVIDER_STROKE[s.provider] }} />
                              {PROVIDER_LABEL[s.provider]}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="h-36 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={ratingSeries} margin={{ top: 6, right: 4, left: 0, bottom: 0 }} accessibilityLayer={false}>
                          <defs>
                            <linearGradient id="reviews-rating-grad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" style={{ stopColor: 'var(--color-primary)', stopOpacity: 0.3 }} />
                              <stop offset="95%" style={{ stopColor: 'var(--color-primary)', stopOpacity: 0.02 }} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" strokeOpacity={0.35} vertical={false} />
                          <XAxis
                            dataKey="day" type="category"
                            tickFormatter={(day) => TICK_FMT.format(new Date(`${day}T00:00:00`))}
                            tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
                            axisLine={false} tickLine={false} minTickGap={32}
                          />
                          <YAxis
                            domain={[(min: number) => Math.max(1, Math.floor((min - 0.05) * 10) / 10), (max: number) => Math.min(5, Math.ceil((max + 0.05) * 10) / 10)]}
                            tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)' }}
                            axisLine={false} tickLine={false} width={28} tickCount={3}
                            tickFormatter={(v) => Number(v).toFixed(1)}
                          />
                          <Tooltip
                            isAnimationActive={false}
                            labelFormatter={(day) => DATE_FMT.format(new Date(`${day}T00:00:00`))}
                            formatter={(v, name) => [`${(v as number).toFixed(2)} ★`, name as string]}
                            contentStyle={{
                              fontSize: 12, borderRadius: 8, border: '1px solid var(--color-border)',
                              background: 'var(--color-card)', color: 'var(--color-foreground)',
                            }}
                          />
                          {pickedSource ? (
                            <Line
                              type="monotone" dataKey={`s_${pickedSource.provider}`}
                              name={`${PROVIDER_LABEL[pickedSource.provider]}, показанная оценка`}
                              stroke={PROVIDER_STROKE[pickedSource.provider]} strokeWidth={2}
                              dot={false} isAnimationActive={false}
                            />
                          ) : (
                            <Area
                              type="monotone" dataKey="v" name="за всё время, по собранным отзывам"
                              stroke="var(--color-primary)" strokeWidth={2}
                              fill="url(#reviews-rating-grad)" dot={false} isAnimationActive={false}
                            />
                          )}
                        </ComposedChart>
                      </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                  {Object.keys(data.period.by_provider).length > 1 && (
                    <div className="space-y-1">
                      {/* Клик по доле источника или по легенде — фильтр ленты по нему (AI27). */}
                      <div className="flex h-2 gap-px overflow-hidden rounded-full bg-muted">
                        {Object.entries(data.period.by_provider).map(([p, n]) => (
                          <button key={p} type="button" onClick={() => pickProvider(p as ReviewProvider)}
                            aria-label={PROVIDER_LABEL[p as ReviewProvider]}
                            className={cn(PROVIDER_BAR[p as ReviewProvider] ?? 'bg-muted-foreground', 'transition-opacity hover:opacity-80',
                              selected?.label === `source:${p}` || !selected?.label.startsWith('source:') ? '' : 'opacity-40')}
                            style={{ width: `${(n / Math.max(1, data.period.count)) * 100}%` }} />
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-x-1 gap-y-1 text-xs text-muted-foreground">
                        {Object.entries(data.period.by_provider).map(([p, n]) => (
                          <button key={p} type="button" onClick={() => pickProvider(p as ReviewProvider)}
                            className={cn('-mx-0.5 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 transition-colors hover:bg-muted hover:text-foreground',
                              selected?.label === `source:${p}` && 'bg-muted text-foreground')}>
                            <span className={cn('size-2 rounded-full', PROVIDER_BAR[p as ReviewProvider])} />
                            {PROVIDER_LABEL[p as ReviewProvider]}: {n}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              )}
            </CardContent>
          </Card>
          {data.reply && data.reply.total > 0 && (
            <ReplyStyleCard
              reply={data.reply}
              analyzing={data.period.pending_analysis > 0}
              unansweredActive={selected?.label === 'без ответа'}
              onToggleUnanswered={() => setSelected(selected?.label === 'без ответа' ? null : {
                label: 'без ответа', ids: unansweredIds, chip: { value: 'без ответа', tone: UNANSWERED_TONE },
              })}
            />
          )}
          </div>
        </div>
        ) : (
          <SourcesStrip sources={data.sources} />
        )
      )}

      <Card variant="section">
        <CardContent className="py-4">
        <SearchChipInput
          className="pb-2"
          draft={draft}
          onDraftChange={setDraft}
          onSubmit={() => setSearch(draft.trim())}
          onReset={() => { setDraft(''); setSearch(''); setSelected(null); }}
          chip={selected?.chip ?? null}
          onClearChip={() => setSelected(null)}
          placeholder="Поиск по тексту отзывов"
          submitLabel="Найти"
          resetLabel="сбросить"
          active={!!search || !!selected}
          extra={<ReviewsChipBuilder data={data} unansweredIds={unansweredIds} onPick={(sel) => { setSelected(sel); setDraft(''); setSearch(''); }} />}
        />
        {(selected || needle) && (
          <p className="pb-2 text-xs text-muted-foreground">
            Найдено: {stream.length} {plural(stream.length, 'отзыв', 'отзыва', 'отзывов')}
            {needle && !selected && ' среди загруженных'}
          </p>
        )}
        {(selected || needle) && stream.length === 0 && (
          <p className="py-6 text-center text-sm text-muted-foreground">Ничего не найдено.</p>
        )}
        {periodEmpty && (
          <NoDataForPeriod section="reviews" what="отзывов" suggestion={{ label: 'Посмотреть за последние 3 месяца', from: isoDay(-90), to: isoDay(0) }} />
        )}
        {stream.map((r) => (
          <div key={r.id}>
            {r.id === firstOlderId && (
              <div className={cn('flex items-center gap-3 pb-2', periodEmpty ? 'pt-12' : 'pt-4')}>
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs uppercase tracking-wider text-muted-foreground">
                  Отзывы до {DATE_FMT.format(new Date(periodStart))}
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}
            <ReviewRow review={r} highlights={highlights} />
          </div>
        ))}

        {!selected && !exhausted && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Загружаем прежние отзывы…
          </div>
        )}
        {stream.length === 0 && !loadingMore && hasAnySource && !selected && !needle && (
          exhausted
            ? <p className="py-10 text-center text-sm text-muted-foreground">Отзывов пока нет совсем</p>
            : <NoDataForPeriod section="reviews" what="отзывов" suggestion={{ label: 'Посмотреть за последние 3 месяца', from: isoDay(-90), to: isoDay(0) }} />
        )}
        {exhausted && stream.length > 0 && (
          <p className="py-3 text-center text-xs text-muted-foreground">Это все отзывы филиала.</p>
        )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Группа тем: подпись над плашками на телефоне, слева колонкой на широком —
 *  обе группы начинаются от одной вертикали (Алексей 16.09, 17.09). */
function ThemeGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-2">
      <span className="shrink-0 text-xs font-medium text-muted-foreground sm:w-28 sm:pt-1.5">{label}</span>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

/** Плашка темы: «Качество кофе · ×3 · −0.13 ★». Подробности — в белой подсказке:
 *  сверху объективные формулировки, ниже мелко цитаты, внизу вклад в среднюю;
 *  по клику — отзывы с жирно выделенными цитатами (Алексей 17.09). */
function ThemeChip({ theme, kind, active, onClick }: {
  theme: ReviewThemeStat;
  kind: 'praise' | 'problem';
  active: boolean;
  onClick: () => void;
}) {
  const label = CATEGORY_LABEL[theme.category] ?? theme.category;
  // Вклад в среднюю сервер отдаёт уже готовым к показу: отрицательным у жалоб,
  // null у похвалы (Алексей 18.09, AI52 — «не складывается в 5.0»). Раньше он
  // приходил как «насколько выше была бы средняя без этих отзывов», и фронт его
  // переворачивал; знак при этом зависел от того, выше или ниже остальных
  // оценивают эти гости, поэтому похвала выходила с минусом, а жалоба с плюсом.
  const impact = theme.rating_impact;
  const content = (
    <div className="space-y-1.5">
      <div className="flex items-baseline gap-2">
        <span className="font-semibold">{label}</span>
        <span className="text-muted-foreground">×{theme.count}</span>
      </div>
      {theme.examples.length > 0 && (
        <ul className="space-y-0.5">
          {theme.examples.map((e) => <li key={e} className="text-foreground/90">— {e}</li>)}
        </ul>
      )}
      {theme.quotes.length > 0 && (
        <div className="space-y-0.5 border-t pt-1.5 text-[11px] text-muted-foreground">
          {theme.quotes.slice(0, 4).map((q) => <div key={`${q.review_id}-${q.text}`}>«{q.text}»</div>)}
        </div>
      )}
      {/* Одна строка вместо таблицы (Алексей 18.09, AI55: «оч сложная таблица…
          надо в "о чём пишут гости" встраивать этот вклад»). Читается как
          «сколько эта тема стоит сейчас» и «сколько вернётся, если её закрыть» —
          это одно и то же число с разными знаками. */}
      <div className="border-t pt-1.5 text-[11px] text-muted-foreground">
        {kind === 'praise'
          ? `${Math.round(theme.share * 100)}% отзывов за период`
          : impact == null || impactText(impact) === '0'
            ? 'на среднюю не влияет: эти гости поставили 5 ★'
            : `сейчас стоит ${impactText(impact)} ★ средней — закроете тему, столько же вернётся`}
      </div>
      <AiDisclaimer />
    </div>
  );
  return (
    <HoverCard content={content} as="span">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors',
          active
            ? (kind === 'praise' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'border-primary bg-primary/5')
            : 'border-border hover:bg-muted',
        )}
      >
        <span>{label}</span>
        <span className="text-xs text-muted-foreground">×{theme.count}</span>
        {impact != null && impactText(impact) !== '0' && (
          <span className={cn('text-xs tabular-nums', deltaClass(impact))}>
            {impactText(impact)} ★
          </span>
        )}
        {kind === 'problem' && theme.severity && theme.severity !== 'minor' && theme.severity !== 'normal' && (
          <span className={cn('rounded-full px-1.5 py-0.5 text-[10px] font-medium', SEVERITY_CLASS[theme.severity])}>
            {SEVERITY_LABEL[theme.severity]}
          </span>
        )}
      </button>
    </HoverCard>
  );
}

/** Текст отзыва с жирно выделенными цитатами выбранной темы. */
function Highlighted({ text, quotes }: { text: string; quotes: string[] }) {
  const low = text.toLowerCase();
  const ranges: [number, number][] = [];
  for (const q of quotes) {
    const ql = q.toLowerCase().trim();
    if (ql.length < 4) continue;
    let i = low.indexOf(ql);
    while (i >= 0) { ranges.push([i, i + ql.length]); i = low.indexOf(ql, i + ql.length); }
  }
  if (!ranges.length) return <>{text}</>;
  ranges.sort((a, b) => a[0] - b[0]);
  const out: React.ReactNode[] = [];
  let pos = 0;
  for (const [s, e] of ranges) {
    if (s < pos) continue;
    out.push(text.slice(pos, s));
    out.push(<mark key={s} className="bg-transparent font-semibold text-foreground">{text.slice(s, e)}</mark>);
    pos = e;
  }
  out.push(text.slice(pos));
  return <>{out}</>;
}

/** Строка ленты. На широком экране — строка таблицы (оценка, дата, источник,
 *  текст, наши темы) с подсветкой по наведению, чтобы было видно, что она
 *  раскрывается; текст раскрывается на месте, а не переезжает ниже — ничего не
 *  прыгает. Автор, ответ и контакт — только в раскрытой (Алексей 17.09, AI17.7). */
function ReviewRow({ review, highlights }: { review: ReviewDto; highlights: string[] }) {
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? highlights.length > 0;
  const date = new Date(review.published_at);
  const whenShort = date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
  const whenTime = date.toLocaleString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  type RowTheme = { kind: 'problem' | 'praise'; label: string; summary: string; severity?: Severity };
  const themes: RowTheme[] = [
    ...(review.analysis?.problems ?? []).map((p): RowTheme => ({ kind: 'problem', label: CATEGORY_LABEL[p.category] ?? p.category, summary: p.summary, severity: p.severity })),
    ...(review.analysis?.praise ?? [])
      .filter((p): p is { category: string; summary: string; quote?: string } => typeof p === 'object')
      .map((p): RowTheme => ({ kind: 'praise', label: CATEGORY_LABEL[p.category] ?? p.category, summary: p.summary })),
  ];
  const unanswered = !review.business_reply && review.provider !== 'linza';
  // В строке — только базовые теги тем («Цены», «Качество кофе»), каждый один
  // раз; конкретика — в раскрытой строке (Алексей 17.09, AI25). Жалоба с той же
  // темой, что и похвала, — отдельный тег своим цветом.
  const SEV_RANK: Record<Severity | 'praise', number> = { praise: 0, minor: 0, normal: 1, major: 2, critical: 3 };
  const baseTags: { key: string; label: string; level: Severity | 'praise' }[] = [];
  for (const t of themes) {
    const key = `${t.kind}:${t.label}`;
    const level = t.kind === 'praise' ? 'praise' : (t.severity ?? 'minor');
    const prev = baseTags.find((b) => b.key === key);
    if (!prev) baseTags.push({ key, label: t.label, level });
    else if (SEV_RANK[level] > SEV_RANK[prev.level]) prev.level = level;
  }
  const unansweredBadge = (
    <span className="whitespace-nowrap rounded bg-amber-100 px-1.5 text-[11px] text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">без ответа</span>
  );
  return (
    <div className={cn('border-b border-border transition-colors last:border-b-0', open && 'bg-muted/25')}>
      <button
        type="button"
        onClick={() => setToggled(!open)}
        aria-expanded={open}
        className="grid w-full cursor-pointer grid-cols-[auto_minmax(0,1fr)_1rem] items-start gap-x-3 gap-y-1.5 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-muted/60 md:grid-cols-[3.5rem_4.25rem_6.5rem_minmax(0,1fr)_minmax(0,1.3fr)_1rem]"
      >
        <span className="inline-flex items-center gap-0.5 text-sm font-medium tabular-nums">
          {review.rating ?? '—'} <Star className="size-3.5 fill-amber-400 text-amber-400" />
        </span>
        {/* Дата и источник: на телефоне — одной строкой рядом с оценкой,
            на широком — отдельными колонками (md:contents). */}
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 text-xs text-muted-foreground md:contents">
          <span className="whitespace-nowrap tabular-nums md:pt-0.5">
            {whenShort}
          </span>
          <span className="whitespace-nowrap md:pt-0.5">{PROVIDER_LABEL[review.provider]}</span>
          {unanswered && <span className="md:hidden">{unansweredBadge}</span>}
        </span>
        <ChevronDown className={cn('mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform md:order-last', open && 'rotate-180')} />
        {/* Текст — один и тот же элемент свёрнутым и раскрытым: снимается только
            обрезка, поэтому строка не прыгает вниз (AI17.7б). */}
        <span className={cn('col-span-3 min-w-0 text-sm md:col-span-1', open ? 'whitespace-pre-wrap break-words' : 'line-clamp-2 text-foreground/80 md:line-clamp-1')}>
          {review.text ? <Highlighted text={review.text} quotes={highlights} /> : <span className="text-muted-foreground">без текста</span>}
        </span>
        {/* Базовые теги тем, фиксированной высоты. */}
        <span className="col-span-3 flex min-w-0 flex-wrap items-center gap-1 md:col-span-1">
          {baseTags.map((b) => (
            <span key={b.key} className={cn('inline-flex h-6 shrink-0 items-center whitespace-nowrap rounded-full border px-2 text-xs font-medium', TAG_TONE[b.level])}>
              {b.label}
            </span>
          ))}
          {unanswered && <span className="hidden md:inline">{unansweredBadge}</span>}
        </span>
      </button>
      {open && (
        <div className="space-y-2 px-2 pb-3 text-sm md:ml-[calc(3.5rem+4.25rem+6.5rem+2.25rem)]">
          <p className="text-xs text-muted-foreground">
            {whenTime}
            {review.author_name && <> · Автор: <span className="text-foreground">{review.author_name}</span></>}
          </p>
          {themes.length > 0 && (
            // Конкретика по темам: «тема │ что именно».
            <div className="flex flex-wrap gap-1">
              {themes.map((t, i) => (
                <SplitPill
                  key={i}
                  kind={t.label}
                  value={t.summary}
                  title={`${t.label}: ${t.summary}`}
                  tone={TAG_TONE[t.kind === 'praise' ? 'praise' : (t.severity ?? 'minor')]}
                />
              ))}
            </div>
          )}
          {review.contact && Object.values(review.contact).some(Boolean) && (
            // Контакт гостя из QR-формы — раньше нигде не показывался.
            <p className="text-xs text-muted-foreground">
              Контакт гостя: <span className="select-all text-foreground">{Object.values(review.contact).filter(Boolean).join(' · ')}</span>
            </p>
          )}
          {review.business_reply && (
            <p className="border-l-2 border-border pl-3 text-xs text-muted-foreground">Ответ заведения: {review.business_reply}</p>
          )}
          {review.url && (
            <a href={review.url} target="_blank" rel="noreferrer"
              className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'h-7 px-2 text-xs')}>
              Открыть <ExternalLink className="size-3" />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

/** Пояснение к графику под значком (i), а не текстом в карточке.
 *
 *  Владелец 22.09 (AI129): «на какое-то (i) показывать пояснения». Раскрытие по
 *  КЛИКУ, а не по ховеру: на телефоне ховера нет, а прошлая шкала ответов жила
 *  подсказками в title, и он справедливо сказал, что на наведение ничего не
 *  показывается.
 *
 *  ВАЖНО: сам текст пояснения — предложение, а не утверждённая формулировка.
 *  Тексты для пользователей согласовываются с владельцем (его правило от 22.09),
 *  так что менять их здесь по своему усмотрению нельзя. */
/** «Оценка сейчас в источниках» — плитки источников. В левой колонке в одну
 *  строку часто не влезает, поэтому заголовок отдельно, а плитки — сеткой с
 *  переносом: 2 в ряд, 1 если не помещаются (Алексей 23.09, #733). */
function SourcesStrip({ sources }: { sources: ReviewSourceDto[] }) {
  return (
    <Card variant="section" className="min-w-0">
      <CardContent className="min-w-0 space-y-2 py-3">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">Оценка сейчас в источниках</span>
        <div className="flex flex-wrap gap-2">
          {sources.map((s) => {
            const change = s.rating != null && s.rating_at_start != null ? s.rating - s.rating_at_start : null;
            const changed = change != null && Math.abs(change) >= 0.005;
            return (
              <a
                key={s.provider}
                href={s.public_url}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border px-2 py-1 text-sm transition-colors hover:bg-muted/60"
              >
                <span className={cn('size-2 shrink-0 rounded-full', PROVIDER_BAR[s.provider])} />
                <span>{PROVIDER_LABEL[s.provider]}</span>
                <span className="font-semibold tabular-nums">{s.rating?.toFixed(1) ?? '—'}</span>
                <Star className="size-3.5 fill-amber-400 text-amber-400" />
                {changed && <span className={cn('text-xs tabular-nums', deltaClass(change))}>{signed(change)} за период</span>}
                {/* Источник считает ОЦЕНКИ (в т.ч. только звёзды) и ОТЗЫВЫ (с текстом) —
                    это разные числа; показываем оба (Алексей 18.09, AI53). */}
                <span className="text-xs text-muted-foreground">
                  {s.rating_count != null && s.rating_count !== s.review_count && (
                    <>{s.rating_count} {plural(s.rating_count, 'оценка', 'оценки', 'оценок')} · </>
                  )}
                  {s.review_count ?? '—'} {plural(s.review_count ?? 0, 'отзыв', 'отзыва', 'отзывов')}
                  {s.stored_count != null && s.review_count != null && s.stored_count < s.review_count && (
                    <>, у нас {s.stored_count}</>
                  )}
                </span>
                {(s.new_in_period ?? 0) > 0 && <span className="text-xs text-primary">+{s.new_in_period} за период</span>}
                {s.last_error && <span className="text-xs text-destructive">не синхронизируется</span>}
                <ExternalLink className="size-3 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </a>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}


/** «Ответы организации» — компактная карточка (Алексей 23.09, AI151).
 *
 *  Спека (владелец, #732): БЕЗ названий осей. Каждый показатель — только слово
 *  текущего уровня, под ним МИКРО-шкала с меткой позиции. Без (i). При наведении
 *  на строку она «вдавливается» и раскрывается подсказка-портал с БОЛЬШОЙ шкалой:
 *  внутри неё можно водить курсором по возможным значениям, и текст под шкалой
 *  меняется, поясняя каждое.
 *
 *  Оценка приходит из СОХРАНЁННОГО разбора отзыва (data.reply): она едет в том же
 *  вызове модели, что и разбор отзыва. Реализацию в подсказках не раскрываем. */

/** Ось оценки: уровни worst→best со своими пояснениями. Ключи совпадают с order
 *  из бэкенда (analysis.REPLY_*_ORDER). Тексты — формулировки владельца 21–23.09;
 *  слово «персонал» под запретом. title держим для отладки, в UI не показываем. */
/** tone — явный цвет уровня, если он не совпадает с долей пути worst→best:
 *  «По теме» уже хорошо, но не «С решением» — зелёный, только приглушённый (Алексей 24.09, AI181);
 *  «Шаблонные» — жёлтый, а не оранжевый (Алексей 24.09). */
type LevelTone = 'red' | 'yellow' | 'amber' | 'green' | 'green-soft';
type AxisMeta = { title: string; levels: Record<string, { label: string; hint: string; tone?: LevelTone }> };
const REPLY_AXES: { key: keyof ReplyAgg['axes']; meta: AxisMeta }[] = [
  {
    key: 'liveliness',
    meta: {
      title: 'Живость',
      levels: {
        copied: { label: 'Скопированные', hint: 'Один и тот же текст под разными отзывами.' },
        template: { label: 'Шаблонные', hint: 'Заготовка с подставленным именем, живого в ней нет.', tone: 'yellow' },
        ai: { label: 'Похоже на нейросеть', hint: 'Подробно проговаривают отзыв и стандарты, гладко и структурно, но холодно и без души. Похоже на нейросеть — по версии самой нейросети :)' },
        lively: { label: 'Живые', hint: 'Отвечают на то, что гость реально написал, своими словами.' },
        warm: { label: 'Тёплые', hint: 'С теплотой и вниманием к конкретному гостю.' },
      },
    },
  },
  {
    key: 'relevance',
    meta: {
      title: 'По существу',
      levels: {
        generic: { label: 'Общие слова', hint: 'Общие фразы, не про сам отзыв.' },
        on_point: { label: 'По теме', hint: 'Отвечают по сути того, что написал гость.', tone: 'green-soft' },
        solution: { label: 'С решением', hint: 'Предлагают, что делать с проблемой.' },
      },
    },
  },
  {
    key: 'tone',
    meta: {
      title: 'Тон',
      levels: {
        rude: { label: 'Грубые', hint: 'Резкие или пренебрежительные ответы.' },
        polite: { label: 'Вежливые', hint: 'Спокойный, уважительный тон.' },
      },
    },
  },
];

/** Тон уровня: явный из меты, иначе по доле пути worst→best. Слово и метка на шкале
 *  красятся одним тоном. */
function levelTone(meta: AxisMeta, key: string | undefined, frac: number): LevelTone {
  const explicit = key ? meta.levels[key]?.tone : undefined;
  if (explicit) return explicit;
  if (frac < 1 / 3) return 'red';
  if (frac < 2 / 3) return 'amber';
  return 'green';
}
const TONE_TEXT: Record<LevelTone, string> = {
  red: 'text-red-600 dark:text-red-400',
  yellow: 'text-yellow-600 dark:text-yellow-400',
  amber: 'text-amber-600 dark:text-amber-400',
  green: 'text-emerald-600 dark:text-emerald-400',
  'green-soft': 'text-green-700/80 dark:text-green-400/80',
};
const TONE_BAR: Record<LevelTone, string> = {
  red: 'bg-red-500',
  yellow: 'bg-yellow-400',
  amber: 'bg-amber-500',
  green: 'bg-emerald-500',
  'green-soft': 'bg-green-500/70',
};
function levelToneText(meta: AxisMeta, key: string | undefined, frac: number): string {
  return TONE_TEXT[levelTone(meta, key, frac)];
}
function levelToneBar(meta: AxisMeta, key: string | undefined, frac: number): string {
  return TONE_BAR[levelTone(meta, key, frac)];
}

/** Микро-шкала под словом: деления по уровням + метка на средней позиции. */
function MicroScale({ axis, meta }: { axis: ReplyAxisAgg; meta: AxisMeta }) {
  if (axis.avg == null) return null;
  const n = axis.order.length;
  const frac = n > 1 ? axis.avg / (n - 1) : 1;
  const cur = axis.order[Math.round(axis.avg)];
  const fill = ((axis.avg + 0.5) / n) * 100;
  // Просто мини-иллюстрация к слову, ровно под ним: короткая полоска с заливкой
  // до позиции. Без кружков и засечек — они были «ту мач» (Алексей 23.09, AI156).
  return (
    <div className="mt-1 h-1 w-12 overflow-hidden rounded-full bg-foreground/10">
      <div className={cn('h-full rounded-full', levelToneBar(meta, cur, frac))} style={{ width: `${fill}%` }} />
    </div>
  );
}

/** Содержимое подсказки: та же шкала, но РАСКРЫТАЯ — все уровни подписаны как
 *  ступени, текущий выделен; наведение на ступень показывает её пояснение внизу
 *  (Алексей 23.09, #745). Паддинг компактный. */
function AxisScaleDetail({ axis, meta }: { axis: ReplyAxisAgg; meta: AxisMeta }) {
  const n = axis.order.length;
  const curIdx = axis.avg == null ? 0 : Math.round(axis.avg);
  const [hover, setHover] = useState<number | null>(null);
  const shown = hover ?? curIdx;
  const shownHint = meta.levels[axis.order[shown]]?.hint;
  return (
    <div className="w-56 p-1.5" onMouseLeave={() => setHover(null)}>
      <div className="space-y-px">
        {axis.order.map((k, i) => {
          const frac = n > 1 ? i / (n - 1) : 1;
          const active = i === shown;
          const isCur = i === curIdx;
          return (
            <button
              key={k}
              type="button"
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              className={cn(
                'flex w-full items-center gap-2 rounded px-1.5 py-1 text-left text-xs transition-colors',
                active ? 'bg-muted' : 'hover:bg-muted/60',
              )}
            >
              <span className={cn('h-1.5 w-5 shrink-0 rounded-full', levelToneBar(meta, k, frac), !isCur && 'opacity-30')} />
              <span className={cn(isCur && 'font-semibold', isCur ? levelToneText(meta, k, frac) : 'text-foreground')}>
                {meta.levels[k]?.label ?? k}
              </span>
              {isCur && <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">сейчас</span>}
            </button>
          );
        })}
      </div>
      <div className="mt-1 border-t border-border px-1.5 pt-1.5 text-[11px] leading-snug text-muted-foreground">{shownHint}</div>
      <AiDisclaimer className="px-1.5" />
    </div>
  );
}

/** Подпись под подсказками, где содержимое — разбор отзывов нейросетью (темы, оценка
 *  ответов): «нейросеть может ошибаться», серым мелким (Алексей 28.09). */
function AiDisclaimer({ className }: { className?: string }) {
  return <div className={cn('pt-1 text-[10px] leading-none text-muted-foreground/70', className)}>нейросеть может ошибаться</div>;
}

/** Одна ось: СЛОВО уровня — главное, тонкая микро-шкала под ним — вспомогательная.
 *  Наведение — обычная подсветка (без «вдавленности», она не вышла) и раскрытая
 *  шкала-подсказка. Названия оси нет (#732, #745). */
function ReplyAxis({ axis, meta }: { axis: ReplyAxisAgg; meta: AxisMeta }) {
  if (axis.avg == null || !axis.n) return null;
  const n = axis.order.length;
  const cur = axis.order[Math.round(axis.avg)] ?? axis.order[n - 1];
  const curMeta = meta.levels[cur];
  const frac = n > 1 ? axis.avg / (n - 1) : 1;
  return (
    <HoverCard
      as="div"
      className="min-w-0 cursor-help rounded-md px-1.5 py-1 transition-colors hover:bg-muted"
      content={<AxisScaleDetail axis={axis} meta={meta} />}
      cardClassName="rounded-lg border border-border bg-card shadow-md"
    >
      <div className={cn('text-sm font-semibold leading-tight', levelToneText(meta, cur, frac))}>{curMeta?.label ?? cur}</div>
      <MicroScale axis={axis} meta={meta} />
    </HoverCard>
  );
}

/** Карточка ответов: ЗАМЕТНАЯ статистика «без ответа» (#743) + три показателя
 *  (слово + микро-шкала, пояснение в подсказке). Пока идёт разбор — лоадер, а не
 *  статичный текст (#742). Без названий осей и без метрики. */
function ReplyStyleCard({
  reply, unansweredActive, onToggleUnanswered, analyzing,
}: {
  reply: ReplyAgg;
  unansweredActive: boolean;
  onToggleUnanswered: () => void;
  analyzing: boolean;
}) {
  return (
    <Card variant="section">
      <CardContent className="space-y-3 py-3">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground">Ответы организации</h3>
        {/* Один ряд, один стиль: «без ответа» — число как слово с подписью под
            ним, дальше слова-уровни с мини-полоской под каждым (AI156). */}
        <div className="flex flex-wrap items-start gap-1.5">
          {reply.unanswered > 0 && (
            <button
              type="button"
              onClick={onToggleUnanswered}
              className={cn(
                'min-w-0 rounded-md px-1.5 py-1 text-left transition-colors',
                unansweredActive ? 'bg-amber-50 dark:bg-amber-900/20' : 'hover:bg-muted',
              )}
            >
              <div className="text-sm font-semibold leading-tight text-amber-600 dark:text-amber-400">{reply.unanswered}</div>
              <div className="mt-1 text-[10px] leading-none text-muted-foreground">без ответа</div>
            </button>
          )}
          {reply.analyzed > 0 && REPLY_AXES.map(({ key, meta }) => (
            <ReplyAxis key={key} axis={reply.axes[key]} meta={meta} />
          ))}
        </div>
        {reply.analyzed === 0 && reply.answered > 0 && (
          analyzing ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" /> Разбираем ответы…
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Оценки ответов появятся после разбора.</p>
          )
        )}
      </CardContent>
    </Card>
  );
}
